import {useMemo, useRef} from 'react';
import {MANAGEMENT_OVERALL_STATUS_VALUES} from '../../../../../core/entities/entryManagements/constants';
import {useGetGroupedSupportCompanies} from '../../../../../core/repositories/companyRepository';
import {
  type GetEntryManagementResponse,
  type GetEntryManagementReturnValueType,
  useUpdateEntryManagement,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {
  showErrorSnackBar,
  showWarningSnackBar,
} from '../../../../../shared/components';
import {
  ENTRY_SOURCE_TYPE_MANUAL,
  type StepCreationTriggerType,
} from '../../../constants/entrySourceTypes';
import {
  getLatestCreatedStepId,
  isLastStep,
  isRejectedOrDeclined,
  shouldShowModal,
} from '../../../usecase/stepUtils';
import type {useAiTodoGeneration} from '../../../usecase/useAiTodoGeneration';
import {useCompanyList} from '../../../usecase/useCompanyList';
import {useEntryManagementUpdate} from '../../../usecase/useEntryManagementUpdate';
import {useHasReportData} from '../../../usecase/useHasReportData';
import type {useProgressDeleteModal} from '../../../usecase/useProgressDeleteModal';
import {useProgressStepReordering} from '../../../usecase/useProgressStepReordering';
import {ProgressDetailPresenter} from '../Presenter/Progress/ProgressDetailPresenter';

/**
 * エラーメッセージ定数
 */
const ERROR_MESSAGES = {
  RESULT_UPDATE: {
    title: '更新できませんでした',
    message: '選考結果の更新に失敗しました',
  },
  STEP_ORDER_UPDATE: {
    title: '更新できませんでした',
    message: 'ステップの並び替えに失敗しました',
  },
  ENTRY_COMPLETION_UPDATE: {
    title: '更新できませんでした',
    message: 'エントリー完了状態の更新に失敗しました',
  },
  OVERALL_STATUS_UPDATE: {
    title: '更新できませんでした',
    message: 'ステータスの更新に失敗しました',
  },
  ENTRY_STATUS_COMBINED_UPDATE: {
    title: '更新できませんでした',
    message: 'エントリー状態とステータスの更新に失敗しました',
  },
  COMPANY_NAME_DUPLICATE: {
    title: '更新できませんでした',
    message: '同じ企業名の選考管理がすでに存在します',
  },
  COMPANY_NAME_UPDATE: {
    title: '更新できませんでした',
    message: '企業名の更新に失敗しました',
  },
  DATE_RANGE_INVALID: {
    title: '入力エラー',
    message: '開始日は終了日より前の日時を設定してください',
  },
} as const;

/**
 * ステップデータをAPI更新用に変換する
 * @param step - 変換対象のステップデータ
 * @returns API送信用のステップオブジェクト
 */
const transformStepForUpdate = (
  step: NonNullable<GetEntryManagementResponse['steps']>[number],
) => ({
  id: step.id,
  stepName: step.stepName,
  stepOrder: step.stepOrder,
  entrySourceType: (step.entrySourceType ||
    ENTRY_SOURCE_TYPE_MANUAL) as StepCreationTriggerType,
});

type Props = {
  entryManagementId: number;
  entryDetailData?: GetEntryManagementReturnValueType;
  existingCompanyNames: string[];
  openInputMethodSelectionModal?: () => void;
  aiTodoGeneration?: ReturnType<typeof useAiTodoGeneration>;
  onEditStep?: (stepData: {
    stepId: number;
    stepOrder: number;
    stepName: string;
    deadline: string;
    result: string | null;
    notes: string;
    startDate?: string;
    endDate?: string;
    place?: string;
  }) => void;
  onDeleteClick: () => void;
  onOpenOverallStatusUpdateModal?: () => void;
  onOpenReportAnalyzeModal?: () => void;
  handleOpenDeleteModal: ReturnType<
    typeof useProgressDeleteModal
  >['handleOpenDeleteModal'];
  hasTodosForEntryManagement?: (entryManagementId: number) => boolean;
  isSp?: boolean;
};

/**
 * 選考管理詳細のContainerコンポーネント
 * ステップの並び替え、更新、削除などのビジネスロジックを管理する
 */
export const ProgressDetailContainer: React.FC<Props> = ({
  entryManagementId,
  entryDetailData,
  existingCompanyNames,
  openInputMethodSelectionModal,
  aiTodoGeneration,
  onEditStep,
  onDeleteClick,
  onOpenOverallStatusUpdateModal,
  onOpenReportAnalyzeModal,
  handleOpenDeleteModal,
  hasTodosForEntryManagement,
  isSp = false,
}) => {
  const updateMutation = useUpdateEntryManagement();
  const {invalidateEntryManagementCache} = useEntryManagementUpdate({
    entryManagementId,
    companyId: entryDetailData?.ok ? entryDetailData.value.companyID || 0 : 0,
    companyName: entryDetailData?.ok ? entryDetailData.value.companyName : '',
    showSuccessMessage: false,
  });

  // 企業データの取得
  const {data: companiesData} = useGetGroupedSupportCompanies({
    queryParams: {},
    config: {
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: 30 * 60 * 1000, // 30分
    },
  });

  // 企業リストを取得（Autocomplete用）
  const companyList = useCompanyList(companiesData, {format: 'autocomplete'});

  const generatingStepIdRef = useRef<number | null>(null);
  const hasShownOverallStatusModal = useRef(false);

  // companyIDの取得
  const companyID = entryDetailData?.ok
    ? (entryDetailData.value.companyID ?? undefined)
    : undefined;

  // ステップデータの取得
  const steps = useMemo(
    () => (entryDetailData?.ok ? (entryDetailData.value.steps ?? []) : []),
    [entryDetailData],
  );

  // ステップの並び替えロジック
  const {reorderedSteps, handleDragEnd, handleMoveUp, handleMoveDown} =
    useProgressStepReordering({
      steps,
      onSaveStepOrder: async (orderedSteps) => {
        if (!entryDetailData?.ok || !orderedSteps) return;
        await updateEntryManagement(
          orderedSteps,
          ERROR_MESSAGES.STEP_ORDER_UPDATE,
        );
      },
    });

  // 体験記・AI選考対策の表示判定
  const {hasReports, hasAiReportSummary} = useHasReportData({companyID});

  // 新規作成ステップの中でcreatedが最新のステップIDを取得
  const latestCreatedStepId = useMemo(
    () => getLatestCreatedStepId(reorderedSteps),
    [reorderedSteps],
  );

  /**
   * エントリー管理を更新する汎用関数
   * @param steps - 更新対象のステップ配列
   * @param errorMessage - エラー時に表示するメッセージ
   * @param additionalParams - 追加の更新パラメータ（エントリー完了状態、全体ステータスなど）
   */
  const updateEntryManagement = async (
    steps: NonNullable<GetEntryManagementResponse['steps']>,
    errorMessage: {title: string; message: string},
    additionalParams: {
      hasCompletedEntry?: boolean;
      overallStatus?: string;
    } = {},
  ) => {
    if (!entryDetailData?.ok) return;

    const entryDetail = entryDetailData.value;
    const updatedSteps = steps.map((step) => transformStepForUpdate(step));

    try {
      const result = await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: entryDetail.companyID || 0,
        companyName: entryDetail.companyName,
        steps: updatedSteps,
        ...additionalParams,
      });

      await invalidateEntryManagementCache();

      if (result.ok && result.value.googleSyncResult?.success === false) {
        showWarningSnackBar({
          title: 'Google同期失敗',
          message:
            '選考管理は更新されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
        });
      }
    } catch (error) {
      console.error('エントリー管理更新エラー:', error);
      showErrorSnackBar(errorMessage);
      throw error;
    }
  };

  if (entryManagementId === 0) {
    return null;
  }

  /**
   * ステップの選考結果を更新するハンドラー
   * 最終ステップで有効な結果が入力された場合、全体ステータス更新モーダルを表示する
   */
  const handleResultChange = async (stepId: number, result: string) => {
    if (!entryDetailData?.ok) return;

    const entryDetail = entryDetailData.value;
    if (!entryDetail.steps) return;

    const targetStep = entryDetail.steps?.find((step) => step.id === stepId);
    if (!targetStep) return;

    try {
      const updateResult = await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: entryDetail.companyID || 0,
        companyName: entryDetail.companyName,
        steps: [
          {
            id: stepId,
            stepName: targetStep.stepName,
            stepOrder: targetStep.stepOrder,
            entrySourceType: (targetStep.entrySourceType ||
              ENTRY_SOURCE_TYPE_MANUAL) as StepCreationTriggerType,
            result: result || null,
          },
        ],
      });

      await invalidateEntryManagementCache();

      if (
        updateResult.ok &&
        updateResult.value.googleSyncResult?.success === false
      ) {
        showWarningSnackBar({
          title: 'Google同期失敗',
          message:
            '選考結果は更新されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
        });
      }

      // モーダル表示判定
      if (entryDetail.steps && entryDetail.steps.length > 0) {
        const targetIsLastStep = isLastStep(
          entryDetail.steps,
          targetStep.stepOrder,
        );
        const targetShouldShowModal = shouldShowModal(result);
        const shouldPromptStatusUpdate = isRejectedOrDeclined(result);

        // 最終ステップの結果が空または「結果待ち」になった場合、モーダル表示フラグをリセット
        if (targetIsLastStep && !targetShouldShowModal) {
          hasShownOverallStatusModal.current = false;
        }

        // 落選・辞退が選択された場合は毎回モーダルを表示
        if (shouldPromptStatusUpdate && onOpenOverallStatusUpdateModal) {
          onOpenOverallStatusUpdateModal();
        }
        // 最終ステップで有効な結果が入力され、まだモーダルを表示していない場合
        else if (
          targetIsLastStep &&
          targetShouldShowModal &&
          !hasShownOverallStatusModal.current &&
          onOpenOverallStatusUpdateModal
        ) {
          hasShownOverallStatusModal.current = true;
          // キャッシュ更新後にモーダルを開く
          onOpenOverallStatusUpdateModal();
        }
      }
    } catch (error) {
      console.error('エントリー管理更新エラー:', error);
      showErrorSnackBar(ERROR_MESSAGES.RESULT_UPDATE);
      throw error;
    }
  };

  /**
   * ステップの各フィールド（ステップ名、期限、開始日時、終了日時、場所、メモ）を更新するハンドラー
   */
  const handleStepFieldUpdate = async (
    stepId: number,
    field:
      | 'stepName'
      | 'deadline'
      | 'startDate'
      | 'endDate'
      | 'place'
      | 'notes',
    value: string | undefined,
  ) => {
    if (!entryDetailData?.ok) return;

    const entryDetail = entryDetailData.value;
    if (!entryDetail.steps) return;

    const targetStep = entryDetail.steps?.find((step) => step.id === stepId);
    if (!targetStep) return;

    // 開始日・終了日の前後関係バリデーション
    if (field === 'startDate' || field === 'endDate') {
      const newStartDate = field === 'startDate' ? value : targetStep.startDate;
      const newEndDate = field === 'endDate' ? value : targetStep.endDate;
      if (
        newStartDate &&
        newEndDate &&
        new Date(newStartDate) > new Date(newEndDate)
      ) {
        showErrorSnackBar(ERROR_MESSAGES.DATE_RANGE_INVALID);
        return;
      }
    }

    try {
      const updateResult = await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: entryDetail.companyID || 0,
        companyName: entryDetail.companyName,
        steps: [
          {
            id: stepId,
            stepName: targetStep.stepName,
            stepOrder: targetStep.stepOrder,
            entrySourceType: (targetStep.entrySourceType ||
              ENTRY_SOURCE_TYPE_MANUAL) as StepCreationTriggerType,
            result: targetStep.result,
            [field]: value === '' || value === undefined ? null : value,
          },
        ],
      });

      await invalidateEntryManagementCache();

      if (
        updateResult.ok &&
        updateResult.value.googleSyncResult?.success === false
      ) {
        showWarningSnackBar({
          title: 'Google同期失敗',
          message:
            '選考管理は更新されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
        });
      }
    } catch (error) {
      console.error(`${field}の更新エラー:`, error);
      showErrorSnackBar({
        title: '更新失敗',
        message: `${field}の更新に失敗しました`,
      });
      throw error;
    }
  };

  /**
   * 全体ステータスを更新するハンドラー
   */
  const handleStatusChange = async (overallStatus: string) => {
    if (!entryDetailData?.ok) return;

    const entryDetail = entryDetailData.value;

    try {
      await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: entryDetail.companyID || 0,
        companyName: entryDetail.companyName,
        overallStatus,
      });

      await invalidateEntryManagementCache();
    } catch (error) {
      console.error('エントリー管理更新エラー:', error);
      showErrorSnackBar(ERROR_MESSAGES.OVERALL_STATUS_UPDATE);
      throw error;
    }
  };

  /**
   * 各ステータスへの変更ハンドラーをまとめたオブジェクト
   */
  const STATUS_HANDLERS = {
    handleChangeToArchiveStatus: () =>
      handleStatusChange(MANAGEMENT_OVERALL_STATUS_VALUES.archived),
    handleChangeToCompleteStatus: () =>
      handleStatusChange(MANAGEMENT_OVERALL_STATUS_VALUES.completed),
    handleChangeToRejectedStatus: () =>
      handleStatusChange(MANAGEMENT_OVERALL_STATUS_VALUES.rejected),
  };

  /**
   * エントリー完了状態と全体ステータスを同時に更新する共通ハンドラー
   * レース条件を回避するため、hasCompletedEntry と overallStatus を1回のAPI呼び出しで更新する
   * @param hasCompletedEntry - エントリー完了状態（true: エントリー済み, false: 未エントリー）
   */
  const handleChangeEntryStatus = async (hasCompletedEntry: boolean) => {
    if (!entryDetailData?.ok) return;

    const entryDetail = entryDetailData.value;

    try {
      await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: entryDetail.companyID || 0,
        companyName: entryDetail.companyName,
        hasCompletedEntry,
        overallStatus: MANAGEMENT_OVERALL_STATUS_VALUES.active,
      });

      await invalidateEntryManagementCache();
    } catch (error) {
      console.error('エントリー状態更新エラー:', error);
      showErrorSnackBar(ERROR_MESSAGES.ENTRY_STATUS_COMBINED_UPDATE);
      throw error;
    }
  };

  /**
   * 未エントリー状態に変更するハンドラー
   * hasCompletedEntry: false と overallStatus: active を1回のAPI呼び出しで更新
   */
  const handleChangeHasCompletedEntryFalse = () =>
    handleChangeEntryStatus(false);

  /**
   * エントリー済み状態に変更するハンドラー
   * hasCompletedEntry: true と overallStatus: active を1回のAPI呼び出しで更新
   */
  const handleChangeHasCompletedEntryTrue = () => handleChangeEntryStatus(true);

  /**
   * AI ToDo生成を実行するハンドラー
   */
  const handleGenerateTodos = (stepId: number) => {
    if (!entryDetailData?.ok || !aiTodoGeneration) return;

    generatingStepIdRef.current = stepId;

    const userEntryManagementID = entryDetailData.value.id;
    const stepName = steps.find((s) => s.id === stepId)?.stepName ?? '';

    aiTodoGeneration.openSelectModal(stepId, userEntryManagementID, stepName);
  };

  /**
   * ステップ削除モーダルを開くハンドラー
   */
  const handleDeleteStepClick = (stepId: number) => {
    handleOpenDeleteModal(stepId, entryManagementId);
  };

  /**
   * 企業名を保存するハンドラー
   * @returns 保存成功時はtrue、失敗時はfalse
   */
  const handleCompanyNameSave = async (
    companyName: string,
    companyId?: number,
  ): Promise<boolean> => {
    if (!entryDetailData?.ok) return false;

    const entryDetail = entryDetailData.value;

    // 既存の企業名の中に同じ企業名が存在するかチェック
    const isDuplicate = existingCompanyNames.some(
      (name) => name === companyName,
    );

    if (isDuplicate) {
      showErrorSnackBar(ERROR_MESSAGES.COMPANY_NAME_DUPLICATE);
      return false;
    }

    try {
      const result = await updateMutation.mutateAsync({
        id: entryManagementId,
        companyID: companyId || 0,
        companyName,
        steps: entryDetail.steps?.map((step) => transformStepForUpdate(step)),
      });

      if (!result.ok) {
        showErrorSnackBar(ERROR_MESSAGES.COMPANY_NAME_UPDATE);
        return false;
      }

      await invalidateEntryManagementCache();

      if (result.value.googleSyncResult?.success === false) {
        showWarningSnackBar({
          title: 'Google同期失敗',
          message:
            '企業名は更新されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
        });
      }
      return true;
    } catch (error) {
      console.error('企業名の更新エラー:', error);
      showErrorSnackBar(ERROR_MESSAGES.COMPANY_NAME_UPDATE);
      return false;
    }
  };

  const generatingStepId = aiTodoGeneration?.isGenerating
    ? generatingStepIdRef.current
    : null;

  return (
    <ProgressDetailPresenter
      entryDetail={entryDetailData?.ok ? entryDetailData.value : undefined}
      reorderedSteps={reorderedSteps}
      latestCreatedStepId={latestCreatedStepId}
      hasReports={hasReports}
      hasAiReportSummary={hasAiReportSummary}
      addStepHandler={openInputMethodSelectionModal}
      editStepHandler={onEditStep}
      resultChangeHandler={handleResultChange}
      stepFieldUpdateHandler={handleStepFieldUpdate}
      onDragEnd={handleDragEnd}
      onDeleteClick={onDeleteClick}
      onChangeToArchiveStatus={STATUS_HANDLERS.handleChangeToArchiveStatus}
      onChangeToCompleteStatus={STATUS_HANDLERS.handleChangeToCompleteStatus}
      onChangeToRejectedStatus={STATUS_HANDLERS.handleChangeToRejectedStatus}
      onChangeHasCompletedEntryFalse={handleChangeHasCompletedEntryFalse}
      onChangeHasCompletedEntryTrue={handleChangeHasCompletedEntryTrue}
      onGenerateTodos={aiTodoGeneration ? handleGenerateTodos : undefined}
      generatingStepId={generatingStepId}
      onOpenReportAnalyzeModal={onOpenReportAnalyzeModal}
      onDeleteStep={handleDeleteStepClick}
      companyNameEditable={true}
      onCompanyNameSave={handleCompanyNameSave}
      companyList={companyList}
      hasTodosForEntryManagement={hasTodosForEntryManagement}
      isSp={isSp}
      onMoveUp={handleMoveUp}
      onMoveDown={handleMoveDown}
    />
  );
};
